<template>
  <div class="device-list-container">
    <div class="list-header">
      <div class="header-top">
        <span class="count">{{ devices.length }} Mezzi</span>
        <button class="close-btn" @click="$emit('close')">
            <XIcon :size="20" />
        </button>
      </div>
      <div class="header-cols">
         <span>Stato</span>
         <span>VIN / Codice</span>
         <span>Azioni</span>
      </div>
    </div>
    <ul class="device-list">
      <li v-for="device in sortedDevices" :key="device.id" class="device-item">
        <!-- Status Icon -->
        <div class="status-col">
           <div class="status-icon" :class="getStatusClass(device)">
             <NavigationIcon v-if="isMoving(device)" class="ico-move" />
             <MapPinIcon v-else class="ico-stop" />
           </div>
        </div>

        <!-- Info -->
        <div class="info-col">
            <div class="vin">{{ device.vin || device.name }}</div>
            <div class="code">{{ device.serial || device.id }}</div>
            <div class="time">{{ formatTime(device.latest_data?.timestamp || device.updatedAt) }}</div>
        </div>

        <!-- Actions -->
        <div class="actions-col">
             <!-- Calendar Button -->
            <button class="action-btn" :class="{ active: calendarDeviceId === device.id }" title="Seleziona data" @click.stop="toggleCalendar(device.id)">
                <CalendarIcon :size="16" />
            </button>

            <button class="action-btn" title="Centra in mappa" @click.stop="$emit('center', device)">
                <MapIcon :size="16" />
            </button>
            <button class="action-btn" :class="{ active: activeTrackId === device.id }" title="Mostra tracciato" @click.stop="$emit('toggle-track', device)">
                <ActivityIcon :size="16" />
            </button>
        </div>
      </li>
    </ul>

    <!-- Shared Calendar Popover -->
    <div v-if="showCalendar" class="sidebar-calendar-popover">
        <div class="calendar-header">
            <span>Seleziona Data</span>
            <button class="close-cal-btn" @click="closeCalendar">✕</button>
        </div>
        <MaterialCalendar 
            :modelValue="selectedDateVal"
            :activityDates="[]"
            @update:modelValue="onDateSelected"
        />
    </div>

  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { NavigationIcon, MapPinIcon, MapIcon, ActivityIcon, XIcon, CalendarIcon } from 'lucide-vue-next';
import MaterialCalendar from '../ui/MaterialCalendar.vue';

const props = defineProps<{
  devices: any[];
  activeTrackId?: number | string | null;
}>();

const emit = defineEmits(['center', 'toggle-track', 'close', 'date-select']);

// Calendar State
const showCalendar = ref(false);
const calendarDeviceId = ref<number | string | null>(null);
const selectedDateVal = ref(new Date().toISOString().split('T')[0]);

const sortedDevices = computed(() => {
    return [...props.devices].sort((a, b) => {
        return (a.vin || '').localeCompare(b.vin || '');
    });
});

function isMoving(device: any): boolean {
    const speed = parseFloat(device.latest_data?.vehicle_speed || '0');
    return speed > 0;
}

function getStatusClass(device: any) {
    return isMoving(device) ? 'moving' : 'stopped';
}

function formatTime(ts: string | number) {
    if (!ts) return '--:--';
    let time = Number(ts);
    if (String(ts).length === 10) time *= 1000; 
    
    const d = new Date(time);
    if (isNaN(d.getTime())) return '--:--';
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function toggleCalendar(devId: number | string) {
    if (calendarDeviceId.value === devId && showCalendar.value) {
        closeCalendar();
    } else {
        calendarDeviceId.value = devId;
        showCalendar.value = true;
    }
}

function closeCalendar() {
    showCalendar.value = false;
    calendarDeviceId.value = null;
}

function onDateSelected(date: string) {
    if (calendarDeviceId.value) {
        emit('date-select', { deviceId: calendarDeviceId.value, date });
        closeCalendar();
    }
}
</script>

<style scoped>
.device-list-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: #1e2329;
    border-left: 1px solid #333;
    color: #eee;
    font-family: 'Inter', sans-serif;
    position: relative; /* For absolute positioning of popover */
}

.list-header {
    padding: 10px;
    background: #2b323b;
    border-bottom: 1px solid #444;
}


.header-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
}

.count {
    font-weight: bold;
    font-size: 1.1em;
}

.close-btn {
    background: none;
    border: none;
    color: #aaa;
    cursor: pointer;
    padding: 5px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.close-btn:hover {
    background: rgba(255,255,255,0.1);
    color: #fff;
}

.header-cols {
    display:flex;
    justify-content: space-between;
    font-size: 0.8em;
    color: #aaa;
    margin-top: 5px;
}

.device-list {
    flex: 1;
    overflow-y: auto;
    list-style: none;
    padding: 0;
    margin: 0;
}

.device-item {
    display: flex;
    align-items: center;
    padding: 8px 10px;
    border-bottom: 1px solid #2a3038;
    gap: 10px;
}

.device-item:hover {
    background: #2a3038;
}

.status-icon {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
}

/* Icons styling */
.ico-move {
    color: #4caf50; 
    width: 20px; 
    height: 20px;
    transform: rotate(45deg); /* Arrow usually points up/right */
}

.ico-stop {
    color: #f44336;
    width: 20px;
    height: 20px;
}

.info-col {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.vin { font-weight: bold; font-size: 0.9em; color: #fff; }
.code { font-size: 0.75em; color: #888; }
.time { font-size: 0.75em; color: #666; }

.actions-col {
    display: flex;
    gap: 5px;
}

.action-btn {
    background: #333;
    border: none;
    color: #ccc;
    width: 30px;
    height: 30px;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}

.action-btn:hover { background: #444; color: #fff; }
.action-btn.active { background: #2196f3; color: white; }

/* sidebar calendar popover */
.sidebar-calendar-popover {
    position: absolute;
    top: 60px; /* offset from header */
    right: 10px; /* float over list */
    width: 320px;
    background: #2b323b;
    border: 1px solid #444;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.5);
    z-index: 1100;
    padding: 10px;
}

.calendar-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    color: #fff;
    font-weight: bold;
}
.close-cal-btn {
    background: none;
    border: none;
    color: #888; 
    cursor: pointer;
    font-size: 1.2rem;
}
.close-cal-btn:hover { color: #fff; }
</style>
